import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MessageCircle, MapPin, Send } from 'lucide-react';
import { OFFICIAL_STUDIO_PROFILE_PATH } from '../../constants/official';
import { submitInquiry } from '../../services/inquiryService';
import './About.css';

// Static studio contact details shown alongside the inquiry form.
const CONTACT_ITEMS: { icon: React.ComponentType<{ className?: string }>; label: string; en: string; value: string }[] = [
  { icon: Mail, label: '官方电子邮箱', en: 'EMAIL', value: 'roc@myloyal.cn' },
  { icon: MessageCircle, label: '官方微信', en: 'WECHAT ID', value: 'fire-fighter' },
  { icon: MapPin, label: '工作室总部', en: 'HQ ADDRESS', value: '成都市锦江区三色路38号成都传媒集团B座7楼' },
];

const INQUIRY_CATEGORIES = ['品牌视觉设计', 'TVC / 宣传片', '电商图 / 视频', 'AI 模型 / 工作流', '其他咨询'];

const ContactSection: React.FC = () => {
  const [form, setForm] = useState({ name: '', brand: '', email: '', phone: '', category: INQUIRY_CATEGORIES[0], brief: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const canSubmit = !!(form.name.trim() && form.email.trim() && form.brief.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await submitInquiry({
      name: form.name.trim(),
      email: form.email.trim(),
      brand: form.brand.trim(),
      phone: form.phone.trim(),
      category: form.category,
      brief: form.brief.trim(),
    });
    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error || '提交失败，请稍后再试');
    }
  };

  return (
    <section className="about-getintouch" aria-labelledby="about-getintouch-title">
      <div className="about-getintouch__intro">
        <p className="about-label">GET IN TOUCH</p>
        <h2 id="about-getintouch-title">开启 AIGC 创意协作</h2>
        <p className="about-getintouch__lead">
          有关于品牌视觉重塑、AI 动态影片定制，或是希望为您的企业深度融入 AIGC 模型技术？填写创意简报，我们将在 24 小时内与您取得联系。
        </p>
        <ul className="about-contact-list">
          {CONTACT_ITEMS.map((c) => (
            <li key={c.en} className="about-contact-item">
              <span className="about-contact-item__icon"><c.icon className="w-5 h-5" /></span>
              <div>
                <p className="about-contact-item__label">{c.label} / {c.en}</p>
                <p className="about-contact-item__value">{c.value}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <form className="about-form" onSubmit={handleSubmit}>
        {submitted ? (
          <div className="about-form__done">
            <div className="about-form__done-icon"><Send className="w-6 h-6" /></div>
            <h3>已收到你的创意简报</h3>
            <p>感谢咨询，我们将在 24 小时内通过邮箱或电话与你联系。</p>
          </div>
        ) : (
          <>
            <div className="about-form__row">
              <label className="about-field">
                <span>您的姓名 / NAME <em>*</em></span>
                <input value={form.name} onChange={set('name')} placeholder="请输入姓名" required />
              </label>
              <label className="about-field">
                <span>公司或品牌名称 / BRAND NAME</span>
                <input value={form.brand} onChange={set('brand')} placeholder="例如：云作创意（选填）" />
              </label>
            </div>
            <div className="about-form__row">
              <label className="about-field">
                <span>电子邮箱 / EMAIL <em>*</em></span>
                <input type="email" value={form.email} onChange={set('email')} placeholder="example@domain.com" required />
              </label>
              <label className="about-field">
                <span>联系电话或微信号 / PHONE OR WECHAT</span>
                <input value={form.phone} onChange={set('phone')} placeholder="方便我们快速联系您（选填）" />
              </label>
            </div>
            <label className="about-field">
              <span>创意协作类别 / INQUIRY CATEGORY <em>*</em></span>
              <select value={form.category} onChange={set('category')}>
                {INQUIRY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="about-field">
              <span>创意协作简报 / BRIEF DESCRIPTION <em>*</em></span>
              <textarea value={form.brief} onChange={set('brief')} rows={5} placeholder="请向我们简单描述您期望合作的视觉方向、所需交付物或项目的预期时间线…" required />
            </label>
            {error && <p className="about-form__error">{error}</p>}
            <button type="submit" className="about-form__submit" disabled={!canSubmit || submitting}>
              <Send className="w-4 h-4" /> {submitting ? '提交中…' : '提交创意简报 / SUBMIT PROPOSAL'}
            </button>
          </>
        )}
      </form>
    </section>
  );
};

const marketSolutions = [
  ['解决选择少', '让需求被更多创作者看见。'],
  ['解决说不清', '用阶段目标拆解交付。'],
  ['解决不放心', '用资金托管保护双方。'],
  ['解决难管理', '让沟通、验收、付款有记录可追踪。'],
];

const studioFits = [
  ['适合重要项目', '需要质量把控，而不是只找一个执行者。'],
  ['适合快速项目', '需要短时间内形成可用视觉方案。'],
  ['适合多版本项目', '需要大量图像、视频、风格测试。'],
  ['适合长期合作', '需要一个稳定理解品牌的 AI 创作团队。'],
];

// 平台创始团队 + 工作室成员. Each member: role, name, and a few lines of bio.
interface TeamMember {
  role: string;
  name: string;
  lines: string[];
}

const founders: TeamMember[] = [
  {
    role: 'CEO',
    name: 'Roc 万礼鹏',
    lines: [
      '本科新加坡国立大学生命科学，硕士佐治亚理工计算机科学。',
      '前亚马逊云服务 RDS 团队、亚马逊全球物流团队软件工程师，6 年开发经验。曾跟随国内头部 AIGC 导演团队创作 6 个月，后兼职担任 a1 Lab 工作室 AIGC 制片人，并成立小茉莉工作室担任 AIGC 制片人。',
      '负责公司战略、产品方向和早期交易闭环落地。',
    ],
  },
  {
    role: 'CTO',
    name: '杨耿',
    lines: [
      '本科中南大学自动化，硕士布朗大学计算机工程。',
      '亚马逊跨境电商团队软件工程师，6 年以上研发经验，深耕大规模分布式系统与生成式 AI 应用，目前专注于基于 LLM 的多智能体系统研发。',
      '主导核心业务流的全栈研发落地。',
    ],
  },
  {
    role: 'CMO',
    name: '张佩琦',
    lines: [
      '前盒马用户运营，前钱大妈营销活动主管，前粗门小程序运营负责人。',
      '专注用户运营、社群运营与用户开发，曾将粗门从 0 用户做到百万注册用户。',
      '负责用户开发、运营增长和产品商业化落地。',
    ],
  },
];

const studioMembers: TeamMember[] = [
  {
    role: '工作室成员',
    name: 'Chao 张朝',
    lines: [
      '可灵 AI 超级创作者，Tapnow 签约导演，8 年高级动画设计师。',
      '全国设计大赛冠军，谷歌官方动画创作者，可灵 AI 视频大赛二等奖。',
    ],
  },
  {
    role: '工作室成员',
    name: 'Max 林鑫亮',
    lines: [
      '海螺 AI 超级创作者，即梦优质创作者，8 年高级动画设计师。',
      '擅长传统动画流程与 AIGC 技术的深度融合，熟悉 AIGC 工作流搭建以及智能体 Skill 开发。',
    ],
  },
  {
    role: '工作室成员',
    name: 'Chris 黄雅图',
    lines: [
      '东京女子美术大学平面设计专业，新锐 AIGC 艺术家。',
      '长期服务品牌设计与视觉内容制作。关注 AI 视觉在品牌传播、虚拟 IP、商业内容与新媒体影像中的落地应用，具备较强的审美判断、创意执行与商业交付能力。',
    ],
  },
  {
    role: '工作室成员',
    name: 'Chloe 应晓云',
    lines: [
      '伦敦艺术大学摄影硕士，中国传媒大学数字艺术学士，新锐 AI 导演。',
      '拥有 8 年以上商业时尚摄影与品牌视觉创作经验，擅长从视觉概念、拍摄执行到后期交付的完整流程，也擅长 AIGC 视觉创作与实拍结合。',
    ],
  },
  {
    role: '商务负责人',
    name: 'Roc 万礼鹏',
    lines: [
      '新加坡国立大学计算机科学硕士，前亚马逊云服务软件工程师，6 年软件开发经验。',
      '曾跟随国内头部 AI 导演创作半年，后担任 a1 Lab 工作室 AIGC 制片人，并创立小茉莉工作室。',
      '具备理工科严密思维，注重细节，同时兼顾艺术追求，擅长梳理需求和统筹团队。',
    ],
  },
];

const About = () => {
  return (
    <div className="about-page">
      <section className="about-hero" aria-labelledby="about-hero-title">
        <img className="about-hero__background" src="/assets/about-hero-cloud-bg-16x9.png" alt="" aria-hidden="true" />
        <div className="about-hero__inner">
          <p className="about-label">关于云作</p>
          <h1 id="about-hero-title">Craftcloud <span className="about-title-accent">云作</span></h1>
          <p className="about-hero__subtitle">开放创作市场 + 自营工作室</p>
        </div>
      </section>

      <main className="about-main">
        <section className="about-twin" aria-labelledby="about-twin-title">
          <div className="about-section-heading">
            <p className="about-label">云端双生</p>
            <h2 id="about-twin-title"><span className="about-title-accent">云作</span>的业务由两部分组成：</h2>
          </div>
          <div className="about-twin__grid">
            <article>
              <span>01</span>
              <h3>创作市场</h3>
              <p>开放的创意云层，让需求方找到更广泛的创作者。</p>
            </article>
            <article>
              <span>02</span>
              <h3>自营工作室</h3>
              <p>由核心团队和签约 AI 艺术家直接承接项目。</p>
            </article>
          </div>
          <p className="about-twin__close">我们称之为：云端双生。</p>
        </section>

        <section className="about-business about-business--market" aria-labelledby="about-market-title">
          <div className="about-business__intro">
            <p className="about-label">创作市场</p>
            <h2 id="about-market-title">创作市场</h2>
            <div className="about-prose">
              <p>如果你需要更快找到创作者，云作市场提供的是一种更透明的合作方式。</p>
              <p>不只靠朋友推荐，不只在社群里碰运气，也不必把小型项目交给流程厚重的代理公司。你可以直接连接创作者，并通过平台完成项目目标、签约、托管和付款。</p>
            </div>
            <Link className="about-cta" to="/">
              去市场逛逛
              <ArrowRight />
            </Link>
          </div>
          <div className="about-business__details">
            <h3>它解决什么</h3>
            <div className="about-rows">
              {marketSolutions.map(([title, text], index) => (
                <article className="about-row" key={title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h4>{title}</h4>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-business about-business--studio" aria-labelledby="about-studio-title">
          <div className="about-business__intro">
            <p className="about-label">自营工作室</p>
            <h2 id="about-studio-title">自营工作室</h2>
            <div className="about-prose">
              <p>如果你需要更完整的创意判断和成片能力，云作工作室可以直接成为你的外部 AI 视觉团队。</p>
              <p>我们服务 TVC、电商图/视频、宣传片、品牌视觉等需求。不同于传统代理的大团队链路，我们更接近一个 AI 原生创作单元：响应快，版本多，试错轻，交付集中。</p>
              <p>项目既可以接入云作平台，用节点、合同和托管来管理，也可以按照传统项目方式推进，由商务和创作团队直接服务。</p>
            </div>
            <Link className="about-cta" to={OFFICIAL_STUDIO_PROFILE_PATH}>
              看看工作室作品
              <ArrowRight />
            </Link>
          </div>
          <div className="about-business__details">
            <h3>它适合什么</h3>
            <div className="about-rows">
              {studioFits.map(([title, text], index) => (
                <article className="about-row" key={title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h4>{title}</h4>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-team" aria-labelledby="about-team-title">
          <div className="about-section-heading">
            <p className="about-label">团队</p>
            <h2 id="about-team-title">平台创始团队</h2>
          </div>

          <div className="about-team__group">
            <h3 className="about-team__group-title">创始团队</h3>
            <div className="about-team__grid">
              {founders.map((m) => (
                <article className="about-member" key={`${m.role}-${m.name}`}>
                  <div className="about-member__head">
                    <span className="about-member__role">{m.role}</span>
                    <h4 className="about-member__name">{m.name}</h4>
                  </div>
                  <div className="about-member__bio">
                    {m.lines.map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="about-team__group">
            <h3 className="about-team__group-title">工作室成员</h3>
            <div className="about-team__grid">
              {studioMembers.map((m) => (
                <article className="about-member" key={`${m.role}-${m.name}`}>
                  <div className="about-member__head">
                    <span className="about-member__role">{m.role}</span>
                    <h4 className="about-member__name">{m.name}</h4>
                  </div>
                  <div className="about-member__bio">
                    {m.lines.map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
    </div>
  );
};

export default About;
